import { useCallback, useRef } from 'react';
import { useDebugPanel } from './panel-context';
import { isDebugPanelEnabled } from './enabled';

export function useTapToOpenDebug(taps = 7, windowMs = 3000): { onClick: () => void } {
  const { open } = useDebugPanel();
  const tapCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTap = useCallback(() => {
    if (!isDebugPanelEnabled()) {
      return;
    }

    tapCountRef.current++;

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
      timerRef.current = null;
    }, windowMs);

    if (tapCountRef.current >= taps) {
      tapCountRef.current = 0;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      open();
    }
  }, [open, taps, windowMs]);

  return { onClick: handleTap };
}

type LongPressHandlers = {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerLeave: () => void;
  onClick: (e: React.MouseEvent) => void;
};

export function useLongPressToOpenDebug(durationMs = 1500): LongPressHandlers {
  const { open } = useDebugPanel();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const didLongPressRef = useRef(false);
  const MOVE_THRESHOLD = 10;

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPosRef.current = null;
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isDebugPanelEnabled()) {
        return;
      }
      startPosRef.current = { x: e.clientX, y: e.clientY };
      didLongPressRef.current = false;

      timerRef.current = setTimeout(() => {
        didLongPressRef.current = true;
        timerRef.current = null;
        open();
      }, durationMs);
    },
    [open, durationMs],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (startPosRef.current === null) {
        return;
      }
      const clientX = e.clientX ?? NaN;
      const clientY = e.clientY ?? NaN;
      const dx = Math.abs(clientX - startPosRef.current.x);
      const dy = Math.abs(clientY - startPosRef.current.y);
      if (Number.isNaN(dx) || Number.isNaN(dy) || dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
        cancel();
      }
    },
    [cancel],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      cancel();
      if (didLongPressRef.current) {
        e.preventDefault();
        didLongPressRef.current = false;
      }
    },
    [cancel],
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (didLongPressRef.current) {
      e.preventDefault();
      didLongPressRef.current = false;
    }
  }, []);

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerLeave: cancel,
    onClick: handleClick,
  };
}
